import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 76 & 77. SCÉNARIOS ENTREPRENEUR / CLIENT
// Le Core ne contient pas les modules : il les alimente. Ces scénarios
// illustrent comment une activité (côté entrepreneur) et un utilisateur
// (côté client) se branchent sur les fondations pour composer leurs usages.
//
//   76. CÔTÉ ENTREPRENEUR — un cabinet d'architecte :
//       Core : Business + Branch + Users + Employees + Customers +
//              Services + Documents + Calendar
//       Modules : Agenda + CRM + Finance + Projects + Documents + AI
//       L'entrepreneur demande « Quels rendez-vous ai-je demain ? » et le
//       Zifek AI Engine utilise les modules via leurs outils sécurisés.
//
//   77. CÔTÉ CLIENT — « Je cherche un architecte à Fès » :
//       search_architects → check_availability → get_reviews → proposition
//       → l'utilisateur confirme → create_appointment.
//       Le Core enregistre la relation :
//       User → Customer → Appointment → Business → Professional → Service
// ─────────────────────────────────────────────────────────────

export type ScenarioSide = 'business' | 'client';

export interface CoreScenario {
  id: number;
  scenario_key: string;
  side: ScenarioSide;
  label: string;
  business_type: string | null;
  summary: string | null;
  core_foundation: string[];
  modules: string[];
  example_questions: string[];
  tool_chain: string[];
  sort_order: number | null;
}

function asArray(v: unknown): string[] {
  return Array.isArray(v) ? (v as string[]) : [];
}

function mapScenario(row: Record<string, unknown>): CoreScenario {
  return {
    id: Number(row.id),
    scenario_key: String(row.scenario_key ?? ''),
    side: ((row.side as string) === 'client' ? 'client' : 'business') as ScenarioSide,
    label: String(row.label ?? ''),
    business_type: (row.business_type as string) ?? null,
    summary: (row.summary as string) ?? null,
    core_foundation: asArray(row.core_foundation),
    modules: asArray(row.modules),
    example_questions: asArray(row.example_questions),
    tool_chain: asArray(row.tool_chain),
    sort_order: row.sort_order != null ? Number(row.sort_order) : null,
  };
}

export const SCENARIO_SIDES: { key: ScenarioSide; label: string; icon: string; desc: string }[] = [
  { key: 'business', label: 'Côté entrepreneur', icon: 'ri-briefcase-4-line', desc: 'Le business compose ses modules au-dessus du Core.' },
  { key: 'client', label: 'Côté client', icon: 'ri-user-smile-line', desc: 'Un utilisateur trouve, compare puis réserve via l’IA.' },
];

/** Relation globale enregistrée par le Core lors d’une réservation client (spec 77). */
export const CLIENT_RELATION_CHAIN: { key: string; label: string; icon: string }[] = [
  { key: 'user', label: 'User', icon: 'ri-user-3-line' },
  { key: 'customer', label: 'Customer', icon: 'ri-user-star-line' },
  { key: 'appointment', label: 'Appointment', icon: 'ri-calendar-check-line' },
  { key: 'business', label: 'Business', icon: 'ri-building-2-line' },
  { key: 'professional', label: 'Professional', icon: 'ri-user-settings-line' },
  { key: 'service', label: 'Service', icon: 'ri-service-line' },
];

export async function listScenarios(): Promise<CoreScenario[]> {
  const { data, error } = await supabase
    .from('core_scenarios')
    .select('*')
    .order('side', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => mapScenario(r as Record<string, unknown>));
}

export async function listScenariosBySide(side: ScenarioSide): Promise<CoreScenario[]> {
  const all = await listScenarios();
  return all.filter((s) => s.side === side);
}

export async function createScenario(input: {
  scenarioKey: string;
  side: ScenarioSide;
  label: string;
  businessType?: string | null;
  summary?: string | null;
  coreFoundation?: string[];
  modules?: string[];
  exampleQuestions?: string[];
  toolChain?: string[];
}): Promise<void> {
  const { error } = await supabase.from('core_scenarios').insert({
    scenario_key: input.scenarioKey.trim().toLowerCase(),
    side: input.side,
    label: input.label,
    business_type: input.businessType || null,
    summary: input.summary || null,
    core_foundation: input.coreFoundation || [],
    modules: input.modules || [],
    example_questions: input.exampleQuestions || [],
    tool_chain: input.toolChain || [],
    sort_order: 90,
  });
  if (error) throw error;
}

export async function deleteScenario(id: number): Promise<void> {
  const { error } = await supabase.from('core_scenarios').delete().eq('id', id);
  if (error) throw error;
}

export interface ScenarioStats {
  total: number;
  business: number;
  client: number;
}

export async function fetchScenarioStats(): Promise<ScenarioStats> {
  const all = await listScenarios();
  return {
    total: all.length,
    business: all.filter((s) => s.side === 'business').length,
    client: all.filter((s) => s.side === 'client').length,
  };
}